import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Row, Col, Card, Button, Spinner } from "react-bootstrap";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";

export default function GalleryUpload({ pId, images: initialImages = [] }) {
    const [images, setImages] = useState([]);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const getAuthHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem("authToken") || localStorage.getItem("AuthUser")}`,
    });

    useEffect(() => {
        setImages(initialImages);
    }, [initialImages]);

    // Helper to resolve image URL dynamically
    const getImageUrl = (item) => {
        const imagePath = item?.image || item?.img || item?.path || "";
        if (!imagePath) return "";
        if (imagePath.startsWith("http") || imagePath.startsWith("blob:")) {
            return imagePath;
        }
        return `${process.env.REACT_APP_IMG_URL}/${imagePath.replace(/^\//, "")}`;
    };

    const handleFileUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        setUploading(true);
        const toastId = toast.loading("Uploading image(s)...");

        try {
            const uploadPromises = files.map(async (file) => {
                const formData = new FormData();
                formData.append("id", pId);
                formData.append("image", file);
                formData.append("model", "products");

                const res = await axios.post(
                    `${process.env.REACT_APP_API_URL}/lister/upload-image`,
                    formData,
                    {
                        headers: {
                            ...getAuthHeaders(),
                            "Content-Type": "multipart/form-data",
                        },
                    }
                );

                return res.data.data;
            });

            const newUploadedImages = await Promise.all(uploadPromises);
            setImages((prev) => [...prev, ...newUploadedImages]);
            toast.success("Image(s) uploaded successfully!", { id: toastId });
        } catch (err) {
            console.error("Upload error:", err);
            toast.error(
                err.response?.data?.message || "Failed to upload image(s).",
                { id: toastId }
            );
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    const deleteImage = async (id) => {
        if (!window.confirm("Are you sure you want to delete this image?")) return;

        const toastId = toast.loading("Deleting image...");

        try {
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/lister/delete-image/${id}`,
                {
                    headers: getAuthHeaders(),
                }
            );

            setImages((prev) => prev.filter((item) => item.id !== id));
            toast.success("Image deleted successfully!", { id: toastId });
        } catch (err) {
            console.error("Delete error:", err);
            toast.error(
                err.response?.data?.message || "Failed to delete image.",
                { id: toastId }
            );
        }
    };

    return (
        <Row className="g-3">
            {/* Existing Images Gallery */}
            {images.map((item) => (
                <Col md={3} sm={6} xs={12} key={item.id}>
                    <Card className="shadow-sm border-0 h-100">
                        <div
                            style={{
                                position: "relative",
                                height: "180px",
                                overflow: "hidden",
                                borderRadius: "0.375rem 0.375rem 0 0",
                            }}
                        >
                            <Card.Img
                                variant="top"
                                src={getImageUrl(item)}
                                alt="Gallery Item"
                                style={{
                                    height: "100%",
                                    width: "100%",
                                    objectFit: "cover",
                                }}
                            />
                        </div>

                        <Card.Body className="text-center d-flex align-items-center justify-content-center p-2">
                            <Button
                                variant="danger"
                                size="sm"
                                className="d-inline-flex align-items-center gap-1"
                                onClick={() => deleteImage(item.id)}
                            >
                                <FiTrash2 size={16} /> Delete
                            </Button>
                        </Card.Body>
                    </Card>
                </Col>
            ))}

            {/* Upload Trigger Dropzone */}
            <Col md={3} sm={6} xs={12}>
                <Card
                    className="shadow-sm d-flex flex-column justify-content-center align-items-center text-center p-3"
                    style={{
                        height: "235px",
                        cursor: uploading ? "not-allowed" : "pointer",
                        border: "2px dashed var(--bs-primary, #0d6efd)",
                        backgroundColor: "#f8f9fa",
                        transition: "all 0.2s ease-in-out",
                    }}
                    onClick={() => !uploading && fileInputRef.current?.click()}
                >
                    {uploading ? (
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" size="sm" />
                            <div className="mt-2 text-muted fw-semibold">Uploading...</div>
                        </div>
                    ) : (
                        <>
                            <div
                                className="bg-primary-subtle text-primary rounded-circle d-flex align-items-center justify-content-center mb-2"
                                style={{ width: "48px", height: "48px" }}
                            >
                                <FiPlus size={24} />
                            </div>
                            <div className="fw-bold text-primary">Add Images</div>
                            <small className="text-muted fs-7">
                                Click to browse files
                            </small>
                        </>
                    )}
                </Card>

                <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    multiple
                    accept="image/*"
                    onChange={handleFileUpload}
                    disabled={uploading}
                />
            </Col>
        </Row>
    );
}